import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralSmartInsightsDetailViewSmartInsightEntryList from 'lumen/components/general/smart-insights/detail-view/smart-insight-entry-list';
import GeneralSmartInsightsDetailViewSmartInsightKpiBlocks from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-blocks';
import type { SmartInsightDay } from 'lumen/models';
import { SMART_INSIGHT_PRIORITIES } from 'lumen/models/smart-insights/smart-insight-day';
import type {
  SmartInsightsArchiveReason,
  SmartInsightsKpiComparisonPeriod,
} from 'lumen/utils/constants/smart-insights';

interface GeneralSmartInsightsDetailViewSmartInsightDetailViewSignature {
  Args: {
    isArchiving?: boolean;
    nextInsight?: { date: string; onReview: () => void };
    onArchive?: (reason: SmartInsightsArchiveReason) => void;
    setSmartInsightsKpiComparisonPeriod: (
      period: SmartInsightsKpiComparisonPeriod,
    ) => void;
    smartInsight: SmartInsightDay;
    smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod;
  };
}

export default class GeneralSmartInsightsDetailViewSmartInsightDetailView extends Component<GeneralSmartInsightsDetailViewSmartInsightDetailViewSignature> {
  get badgeColorClass(): string {
    const { smartInsight } = this.args;

    switch (smartInsight.stayDatePriority) {
      case SMART_INSIGHT_PRIORITIES.HIGH:
        return 'smart-insight-detail-view__badge--with-high-priority';
      case SMART_INSIGHT_PRIORITIES.MEDIUM:
        return 'smart-insight-detail-view__badge--with-medium-priority';
      case SMART_INSIGHT_PRIORITIES.LOW:
        return 'smart-insight-detail-view__badge--with-low-priority';
      default:
        return '';
    }
  }

  get hasInsightEntries(): boolean {
    return this.args.smartInsight.insightEntriesSortedByRelevance.length > 0;
  }

  get showNextInsightRow(): boolean {
    return !!this.args.nextInsight;
  }

  <template>
    <div
      class="smart-insight-detail-view flex flex-col gap-600 p-400 pb-[80px]"
      data-test-id="smart-insight-detail-view"
    >
      {{#if this.hasInsightEntries}}
        <GeneralSmartInsightsDetailViewSmartInsightEntryList
          @isArchiving={{@isArchiving}}
          @onArchive={{@onArchive}}
          @openInNewTab={{false}}
          @product="smart-insights"
          @smartInsightDay={{@smartInsight}}
        />
      {{else}}
        <div
          class="rounded-200 bg-elevation-surface p-500 shadow-neutral-100"
          data-test-id="smart-insight-detail-view-no-insights"
        >
          <p class="text-300--regular">
            {{t
              "smart_insights.empty_state.description"
              classNameBold="font-bold"
              htmlSafe=true
            }}
          </p>
          {{#if this.showNextInsightRow}}
            <div class="divider-line mt-500 mb-300" />
            <div class="flex items-center justify-between gap-200">
              <div class="flex items-center gap-200">
                <PrismIcon @color="on-intelligence" @name="smart-insights" />
                <span class="text-300--regular">
                  {{t
                    "smart_insights.empty_state.next_insight"
                    classNameBold="font-bold"
                    date=@nextInsight.date
                    htmlSafe=true
                  }}
                </span>
              </div>

              <PrismButton
                @buttonType="primary"
                @isSmall={{true}}
                @label={{t "smart_insights.empty_state.review"}}
                @onClick={{@nextInsight.onReview}}
                data-test-id="review-next-insight-button"
              />
            </div>
          {{/if}}
        </div>
      {{/if}}

      <GeneralSmartInsightsDetailViewSmartInsightKpiBlocks
        @setSmartInsightsKpiComparisonPeriod={{@setSmartInsightsKpiComparisonPeriod}}
        @smartInsightDay={{@smartInsight}}
        @smartInsightsKpiComparisonPeriod={{@smartInsightsKpiComparisonPeriod}}
      />
    </div>
  </template>
}
